import { ChevronDownIcon } from 'lucide-react'
import { PlusCircleIcon, StarIcon } from '@heroicons/react/16/solid'
import { Link, useNavigate } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Button } from '@/components/ui/button'
import useAuthStore from '@/store/useAuthStore'
import useGetTeams from '@/services/api/team/useGetTeams'
import useSwitchTeam from '@/services/api/team/useSwitchTeam'
import useSetDefaultTeam from '@/services/api/team/useSetDefaultTeam'

export default function SpacesMenu() {
  const navigate = useNavigate()
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const { data: teams } = useGetTeams()
  const { action: switchTeam, status: switchTeamStatus } = useSwitchTeam()
  const { action: setDefaultTeam, status: defaultTeamStatus } =
    useSetDefaultTeam()

  const currentTeam = teams?.find((team) => team.id === currentTeamId)

  async function handleSwitchTeam(id: string | number) {
    navigate('/')
    await switchTeam({ teamId: id })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 items-center gap-2 rounded-[9px] border border-primary bg-white px-2.5 py-1.5 text-sm font-semibold text-txt-primary"
        >
          <span className="truncate">{currentTeam?.name ?? '—'}</span>
          {teams?.length > 0 && (
            <span className="whitespace-nowrap text-xs font-normal text-txt-secondary">
              {teams.length} space{teams.length === 1 ? '' : 's'}
            </span>
          )}
          <ChevronDownIcon className="size-3.5 shrink-0 text-txt-secondary" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-[300px]" align="start">
        <DropdownMenuLabel>My spaces</DropdownMenuLabel>
        {teams?.map((team) => (
          <DropdownMenuItem
            key={team.id}
            data-loading={
              [defaultTeamStatus.status, switchTeamStatus.status].includes(
                'pending',
              )
                ? true
                : undefined
            }
            data-active={team.id === currentTeamId ? true : undefined}
            className="group flex items-center justify-between gap-2 data-[active]:bg-green-tr-12 data-[active]:text-txt-success data-[loading]:pointer-events-none data-[loading]:animate-pulse"
            onSelect={(e) => {
              e.preventDefault()
              if (team.id !== currentTeamId) handleSwitchTeam(team.id)
            }}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {team.is_default ? (
                <StarIcon className="size-4 shrink-0 text-blue-500" />
              ) : (
                <Button
                  className="hidden h-max w-max shrink-0 p-0 group-hover:block"
                  iconOnly
                  variant="ghost"
                  title="Make default"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDefaultTeam({ teamId: team.id })
                  }}
                >
                  <StarIcon className="size-4 text-blue-500" />
                </Button>
              )}
              <span className="truncate">{team.name}</span>
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/create-space"
            className="flex items-center gap-2 text-txt-accent"
          >
            <PlusCircleIcon className="size-4" />
            New space
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
